import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Pencil, Trash2, Send, Plus } from 'lucide-react';
import type {
  Paginated,
  TimesheetEntryInput,
  TimesheetState,
} from '@timesheet/shared';
import { apiOrThrow } from '@/lib/api';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card, CardBody } from '@/components/ui/Card';
import { Alert } from '@/components/ui/Alert';
import { StateBadge } from '@/components/timesheet/StateBadge';
import { EntryFormModal } from '@/components/timesheet/EntryFormModal';

interface EntryRow {
  id: string;
  workDate: string;
  clientCode: string | null;
  clientName: string | null;
  engagementCode: string | null;
  engagementName: string | null;
  activityCode: string | null;
  activityName: string | null;
  hours: number;
  description: string | null;
  state: TimesheetState;
  rejectionReason: string | null;
}

function todayDate(): string {
  return new Date().toISOString().slice(0, 10);
}
function daysAgo(n: number): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - n);
  return d.toISOString().slice(0, 10);
}

export function TimesheetPage() {
  const qc = useQueryClient();
  const [fromDate, setFromDate] = useState(daysAgo(30));
  const [toDate, setToDate] = useState(todayDate());
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<EntryRow | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const listQuery = useQuery({
    queryKey: ['timesheet', 'list', { fromDate, toDate }],
    queryFn: () => {
      const params = new URLSearchParams({ fromDate, toDate, pageSize: '500' });
      return apiOrThrow<Paginated<EntryRow>>(`/timesheet?${params.toString()}`);
    },
  });

  const createMut = useMutation({
    mutationFn: (input: TimesheetEntryInput) =>
      apiOrThrow<EntryRow>('/timesheet', { method: 'POST', json: input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['timesheet'] }),
  });

  const updateMut = useMutation({
    mutationFn: (vars: { id: string; input: TimesheetEntryInput }) =>
      apiOrThrow<EntryRow>(`/timesheet/${vars.id}`, { method: 'PATCH', json: vars.input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['timesheet'] }),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) =>
      apiOrThrow<{ id: string }>(`/timesheet/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['timesheet'] }),
  });

  const submitMut = useMutation({
    mutationFn: (entryIds: string[]) =>
      apiOrThrow<{ transitioned: number }>('/timesheet/transition', {
        method: 'POST',
        json: { entryIds, toState: 'submitted' },
      }),
    onSuccess: () => {
      setSelected(new Set());
      qc.invalidateQueries({ queryKey: ['timesheet'] });
    },
  });

  const items = listQuery.data?.items ?? [];

  const draftSelected = useMemo(
    () => items.filter((e) => e.state === 'draft' && selected.has(e.id)),
    [items, selected],
  );

  // Totals by state (small dashboard)
  const totals = useMemo(() => {
    const t = {
      total: 0,
      draft: 0,
      submitted: 0,
      managerApproved: 0,
      partnerApproved: 0,
      rejected: 0,
    };
    for (const e of items) {
      t.total += e.hours;
      if (e.state === 'draft') t.draft += e.hours;
      else if (e.state === 'submitted') t.submitted += e.hours;
      else if (e.state === 'manager_approved') t.managerApproved += e.hours;
      else if (e.state === 'partner_approved') t.partnerApproved += e.hours;
      else if (e.state === 'rejected') t.rejected += e.hours;
    }
    return t;
  }, [items]);

  const toggleSelect = (id: string) => {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAllDrafts = () => {
    const allDrafts = items.filter((e) => e.state === 'draft').map((e) => e.id);
    setSelected(new Set(allDrafts));
  };

  const columns: Column<EntryRow>[] = [
    {
      key: 'select',
      header: '',
      render: (r) =>
        r.state === 'draft' ? (
          <input
            type="checkbox"
            checked={selected.has(r.id)}
            onChange={() => toggleSelect(r.id)}
            className="h-4 w-4 rounded border-brand-gray-light text-brand-red"
            aria-label={`Select entry for ${r.workDate}`}
          />
        ) : null,
      className: 'w-8',
    },
    { key: 'workDate', header: 'Date', className: 'whitespace-nowrap font-mono text-xs' },
    {
      key: 'client',
      header: 'Client / Engagement',
      render: (r) => (
        <div className="min-w-0">
          <div className="truncate font-medium text-brand-charcoal">{r.clientName ?? '—'}</div>
          <div className="truncate text-xs text-brand-gray-mid">
            {r.engagementCode} · {r.engagementName}
          </div>
        </div>
      ),
      grow: true,
    },
    {
      key: 'activity',
      header: 'Activity',
      render: (r) => <span className="text-xs">{r.activityName ?? '—'}</span>,
    },
    {
      key: 'hours',
      header: 'Hours',
      render: (r) => <span className="font-mono font-medium">{r.hours.toFixed(2)}</span>,
      className: 'whitespace-nowrap text-right',
    },
    {
      key: 'state',
      header: 'Status',
      render: (r) => (
        <div>
          <StateBadge state={r.state} />
          {r.state === 'rejected' && r.rejectionReason && (
            <p className="mt-1 text-xs text-brand-danger">{r.rejectionReason}</p>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="mx-auto max-w-6xl space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-brand-charcoal">My timesheet</h1>
          <p className="mt-1 text-sm text-brand-gray-mid">
            Enter hours by day, submit a batch for approval.
          </p>
        </div>
        <Button onClick={() => setCreating(true)}>
          <Plus className="h-4 w-4" aria-hidden />
          <span>New entry</span>
        </Button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
        <SummaryCard label="Total hours" value={totals.total} tone="neutral" />
        <SummaryCard label="Draft" value={totals.draft} tone="neutral" />
        <SummaryCard label="Submitted" value={totals.submitted} tone="info" />
        <SummaryCard label="Approved" value={totals.partnerApproved} tone="success" />
        <SummaryCard label="Rejected" value={totals.rejected} tone="danger" />
      </div>

      {/* Filters + bulk action */}
      <Card>
        <CardBody className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs font-medium text-brand-gray-mid">From</label>
            <Input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="w-40"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-brand-gray-mid">To</label>
            <Input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="w-40"
            />
          </div>
          <div className="ml-auto flex items-center gap-2">
            {draftSelected.length === 0 ? (
              <Button variant="ghost" size="sm" onClick={selectAllDrafts}>
                Select all drafts
              </Button>
            ) : (
              <>
                <span className="text-sm text-brand-gray-mid">
                  {draftSelected.length} selected
                </span>
                <Button
                  variant="primary"
                  size="md"
                  loading={submitMut.isPending}
                  onClick={() => {
                    if (
                      confirm(
                        `Submit ${draftSelected.length} draft entr${draftSelected.length === 1 ? 'y' : 'ies'} for approval?`,
                      )
                    ) {
                      submitMut.mutate(draftSelected.map((e) => e.id));
                    }
                  }}
                >
                  <Send className="h-4 w-4" aria-hidden />
                  <span>Submit</span>
                </Button>
              </>
            )}
          </div>
        </CardBody>
      </Card>

      {submitMut.isError && (
        <Alert tone="danger">{(submitMut.error as Error).message}</Alert>
      )}

      <DataTable
        columns={columns}
        rows={items}
        loading={listQuery.isPending}
        total={listQuery.data?.total}
        rowKey={(r) => r.id}
        emptyMessage="No entries in this date range yet. Click 'New entry' to start."
        actions={(r) =>
          r.state === 'draft' ? (
            <div className="flex justify-end gap-1">
              <Button variant="ghost" size="sm" onClick={() => setEditing(r)}>
                <Pencil className="h-3.5 w-3.5" aria-hidden />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  if (confirm('Delete this draft entry?')) deleteMut.mutate(r.id);
                }}
              >
                <Trash2 className="h-3.5 w-3.5 text-brand-danger" aria-hidden />
              </Button>
            </div>
          ) : null
        }
      />

      <EntryFormModal
        open={creating}
        onClose={() => setCreating(false)}
        title="New timesheet entry"
        onSubmit={async (input) => {
          await createMut.mutateAsync(input);
          setCreating(false);
        }}
      />

      <EntryFormModal
        key={editing?.id ?? 'closed-edit'}
        open={!!editing}
        onClose={() => setEditing(null)}
        title={`Edit entry · ${editing?.workDate ?? ''}`}
        defaultValues={
          editing
            ? {
                workDate: editing.workDate,
                clientId: '',
                engagementId: '',
                activityId: '',
                hours: editing.hours,
                description: editing.description ?? '',
              }
            : undefined
        }
        onSubmit={async (input) => {
          if (!editing) return;
          await updateMut.mutateAsync({ id: editing.id, input });
          setEditing(null);
        }}
      />
    </div>
  );
}

function SummaryCard({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: 'neutral' | 'info' | 'success' | 'danger';
}) {
  const toneClass = {
    neutral: 'text-brand-charcoal',
    info: 'text-brand-blue',
    success: 'text-brand-success',
    danger: 'text-brand-danger',
  }[tone];
  return (
    <Card>
      <CardBody className="!py-3">
        <p className="text-xs text-brand-gray-mid">{label}</p>
        <p className={`text-2xl font-semibold ${toneClass}`}>{value.toFixed(2)}h</p>
      </CardBody>
    </Card>
  );
}
